import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Users, Calendar, CheckCircle,
  XCircle, Clock, Edit3, Trash2, Filter, TrendingUp, UserPlus, FileText, CalendarPlus, MessageSquare
} from 'lucide-react';
import { firstContactService, clientService } from '../services/api';
import toast from 'react-hot-toast';
import QuickClientModal from './QuickClientModal';
import AvailableSlotsModal from './AvailableSlotsModal';
import { useAuth } from '../contexts/AuthContext';

const ClientFirstContact = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Estados
  const [showForm, setShowForm] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    service_type: '',
    is_referral: '',
    is_second_work: '',
    search: '',
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, item: null });

  // Formulário
  const [formData, setFormData] = useState({
    client_id: '',
    contact_date: new Date().toISOString().split('T')[0],
    contact_source: '',
    is_referral: false,
    referred_by_client_id: '',
    referred_by_name: '',
    service_type: '',
    environment_count: 1,
    is_multi_environment: false,
    is_second_work: false,
    estimated_budget: '',
    notes: '',
    status: 'NOVO',
    follow_up_date: '',
    desired_deadline: '',
    origin_type: 'NOVO',
    potential: 'MEDIO',
  });

  // Buscar clientes para seleção
  const { data: clientsData } = useQuery({
    queryKey: ['clients-search', clientSearch],
    queryFn: () => clientService.getClients({ search: clientSearch, limit: 20 }),
    enabled: clientSearch.length >= 2,
  });

  const clients = useMemo(() => {
    if (!clientsData?.data?.data) return [];
    return clientsData.data.data;
  }, [clientsData]);

  // Buscar primeiros contatos
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['first-contacts', filters],
    queryFn: () => firstContactService.getFirstContacts(filters),
    staleTime: 0,
  });

  // Estatísticas
  const { data: statsData } = useQuery({
    queryKey: ['first-contacts-stats'],
    queryFn: () => firstContactService.getStats(),
  });

  const firstContacts = useMemo(() => {
    if (!data?.data?.data?.first_contacts) return [];
    return data.data.data.first_contacts;
  }, [data]);

  const pagination = useMemo(() => {
    return data?.data?.data?.pagination || { page: 1, limit: 20, total: 0 };
  }, [data]);

  const stats = useMemo(() => {
    return statsData?.data?.data || {};
  }, [statsData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => firstContactService.createFirstContact(data),
    onSuccess: () => {
      toast.success('Primeiro contato registrado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['first-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['first-contacts-stats'] });
      refetch(); // Forçar recarregamento imediato
      handleCloseForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar primeiro contato');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => firstContactService.updateFirstContact(id, data),
    onSuccess: () => {
      toast.success('Primeiro contato atualizado!');
      queryClient.invalidateQueries({ queryKey: ['first-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['first-contacts-stats'] });
      handleCloseForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => firstContactService.deleteFirstContact(id),
    onSuccess: () => {
      toast.success('Primeiro contato excluído!');
      queryClient.invalidateQueries({ queryKey: ['first-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['first-contacts-stats'] });
      setDeleteConfirm({ show: false, item: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir');
    },
  });

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setSelectedClient(null);
    setClientSearch('');
    setFormData({
      client_id: '',
      contact_date: new Date().toISOString().split('T')[0],
      contact_source: '',
      is_referral: false,
      referred_by_client_id: '',
      referred_by_name: '',
      service_type: '',
      environment_count: 1,
      is_multi_environment: false,
      is_second_work: false,
      estimated_budget: '',
      notes: '',
      status: 'NOVO',
      follow_up_date: '',
      desired_deadline: '',
      origin_type: 'NOVO',
      potential: 'MEDIO',
    });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setSelectedClient({
      id: item.client_id,
      full_name: item.client_name,
    });
    setClientSearch(item.client_name || '');
    setFormData({
      client_id: item.client_id,
      contact_date: item.contact_date?.split('T')[0] || '',
      contact_source: item.contact_source || '',
      is_referral: item.is_referral || false,
      referred_by_client_id: item.referred_by_client_id || '',
      referred_by_name: item.referred_by_name || '',
      service_type: item.service_type || '',
      environment_count: item.environment_count || 1,
      is_multi_environment: item.is_multi_environment || false,
      is_second_work: item.is_second_work || false,
      estimated_budget: item.estimated_budget || '',
      notes: item.notes || '',
      status: item.status || 'NOVO',
      follow_up_date: item.follow_up_date?.split('T')[0] || '',
      desired_deadline: item.desired_deadline?.split('T')[0] || '',
      origin_type: item.origin_type || 'NOVO',
      potential: item.potential || 'MEDIO',
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Selecione um cliente!');
      return;
    }

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      NOVO: { bg: 'bg-blue-100', text: 'text-blue-800', icon: UserPlus },
      EM_NEGOCIACAO: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      CONVERTIDO: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      PERDIDO: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
    };
    const badge = badges[status] || badges.NOVO;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {status?.replace('_', ' ')}
      </span>
    );
  };

  const getServiceTypeBadge = (type) => {
    const badges = {
      PEQUENO: { bg: 'bg-green-100', text: 'text-green-800' },
      MEDIO: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      GRANDE: { bg: 'bg-red-100', text: 'text-red-800' },
    };
    const badge = badges[type] || {};
    return type ? (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {type}
      </span>
    ) : null;
  };

  const getOriginBadge = (origin) => {
    const badges = {
      NOVO: { bg: 'bg-green-100', text: 'text-green-800', label: 'Novo' },
      ANTIGO: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Antigo' },
      INDICACAO: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Indicação' },
    };
    const badge = badges[origin] || badges.NOVO;
    return origin ? (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    ) : null;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Primeiro Contato</h1>
          <p className="text-gray-600">Qualificação inicial de clientes</p>
        </div>
        {user.role !== 'RECEPCAO_PROJETOS' && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630]"
          >
            <Plus className="w-4 h-4" />
            Novo Contato
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-600 font-medium">Total</p>
          <p className="text-2xl font-bold text-blue-800">{stats.total || 0}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-sm text-yellow-600 font-medium">Em Negociação</p>
          <p className="text-2xl font-bold text-yellow-800">{stats.em_negociacao_count || 0}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">Convertidos</p>
          <p className="text-2xl font-bold text-green-800">{stats.convertido_count || 0}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-sm text-purple-600 font-medium">Indicações</p>
          <p className="text-2xl font-bold text-purple-800">{stats.referrals_count || 0}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-sm text-orange-600 font-medium">2º Trabalho</p>
          <p className="text-2xl font-bold text-orange-800">{stats.second_work_count || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todos Status</option>
            <option value="NOVO">Novo</option>
            <option value="EM_NEGOCIACAO">Em Negociação</option>
            <option value="CONVERTIDO">Convertido</option>
            <option value="PERDIDO">Perdido</option>
          </select>
          <select
            value={filters.service_type}
            onChange={(e) => setFilters({ ...filters, service_type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todos Tipos</option>
            <option value="PEQUENO">Pequeno</option>
            <option value="MEDIO">Médio</option>
            <option value="GRANDE">Grande</option>
          </select>
          <select
            value={filters.is_referral}
            onChange={(e) => setFilters({ ...filters, is_referral: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todas Origens</option>
            <option value="true">Indicação</option>
            <option value="false">Outros</option>
          </select>
          <select
            value={filters.is_second_work}
            onChange={(e) => setFilters({ ...filters, is_second_work: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todos Trabalhos</option>
            <option value="true">2º Trabalho</option>
            <option value="false">Primeiro</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : firstContacts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhum primeiro contato encontrado.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Cliente</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Data</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Serviço</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Ambientes</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Orçamento</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {firstContacts.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.client_name}</p>
                      <div className="flex gap-1 mt-1">
                        {item.is_referral && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            Indicação
                          </span>
                        )}
                        {item.is_second_work && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                            2º Trabalho
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.contact_date ? new Date(item.contact_date).toLocaleDateString('pt-BR') : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {getServiceTypeBadge(item.service_type)}
                  </td>
                  <td className="px-4 py-3">
                    {getOriginBadge(item.origin_type)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.is_multi_environment ? 'Múltiplos' : item.environment_count}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(item.status)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.estimated_budget
                      ? Number(item.estimated_budget).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {item.client_phone && (
                        <a
                          href={`https://wa.me/55${item.client_phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá, ${item.client_name}! Sou da Marcenaria Santiago. Gostaria de conversar sobre o seu projeto de móveis planejado.`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Conversar no WhatsApp"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => navigate(`/agendamentos/novo?type=levantamento&first_contact_id=${item.id}&client_id=${item.client_id || ''}&client_name=${encodeURIComponent(item.client_name || '')}`)}
                        className="p-1.5 text-[#49943d] hover:bg-green-50 rounded-lg transition-colors"
                        title="Agendar Visita / Levantamento"
                      >
                        <CalendarPlus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ show: true, item: item })}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Confirmar Exclusão</h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja excluir o primeiro contato de{' '}
                <strong>{deleteConfirm.item?.client_name}</strong>?
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDeleteConfirm({ show: false, item: null })}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => deleteMutation.mutate(deleteConfirm.item.id)}
                  disabled={deleteMutation.isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isLoading ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Editar Primeiro Contato' : 'Novo Primeiro Contato'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Cliente */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Cliente *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowClientModal(true)}
                    className="text-sm text-[#49943d] hover:text-[#3a7630] font-medium"
                  >
                    + Novo Cliente
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setFormData({ ...formData, client_id: '' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  disabled={!!selectedClient}
                />
                {clients.length > 0 && !selectedClient && (
                  <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                    {clients.map((client) => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => {
                          setSelectedClient(client);
                          setFormData({ ...formData, client_id: client.id });
                          setClientSearch('');
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm"
                      >
                        {client.full_name} - {client.email}
                      </button>
                    ))}
                  </div>
                )}
                {selectedClient && (
                  <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 rounded-lg">
                    <Users className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800">{selectedClient.full_name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedClient(null);
                        setFormData({ ...formData, client_id: '' });
                      }}
                      className="ml-auto text-green-600 hover:text-green-800"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Data do Contato */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data do Contato
                  </label>
                  <input
                    type="date"
                    value={formData.contact_date}
                    onChange={(e) => setFormData({ ...formData, contact_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Canal de Captação
                  </label>
                  <select
                    value={formData.contact_source}
                    onChange={(e) => setFormData({ ...formData, contact_source: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Selecione...</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Google">Google</option>
                    <option value="Obra Vizinha">Obra Vizinha</option>
                    <option value="Telefone">Telefone</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>

              {/* Tipo de Serviço */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Qualificação do Serviço</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white">
                    <input
                      type="radio"
                      name="service_type"
                      value="PEQUENO"
                      checked={formData.service_type === 'PEQUENO'}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium text-sm">Pequeno</p>
                      <p className="text-xs text-gray-500">mesa, pequeno armário</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white">
                    <input
                      type="radio"
                      name="service_type"
                      value="MEDIO"
                      checked={formData.service_type === 'MEDIO'}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium text-sm">Médio</p>
                      <p className="text-xs text-gray-500">1 ambiente</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white">
                    <input
                      type="radio"
                      name="service_type"
                      value="GRANDE"
                      checked={formData.service_type === 'GRANDE'}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium text-sm">Grande</p>
                      <p className="text-xs text-gray-500">+ de 1 ambiente</p>
                    </div>
                  </label>
                </div>

                {/* Potencial */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Potencial de Fechamento
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white">
                      <input
                        type="radio"
                        name="potential"
                        value="ALTO"
                        checked={formData.potential === 'ALTO'}
                        onChange={(e) => setFormData({ ...formData, potential: e.target.value })}
                        className="w-4 h-4"
                      />
                      <div>
                        <p className="font-medium text-sm text-green-700">Alto</p>
                        <p className="text-xs text-gray-500">Alta chance de fechar</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white">
                      <input
                        type="radio"
                        name="potential"
                        value="MEDIO"
                        checked={formData.potential === 'MEDIO'}
                        onChange={(e) => setFormData({ ...formData, potential: e.target.value })}
                        className="w-4 h-4"
                      />
                      <div>
                        <p className="font-medium text-sm text-yellow-700">Médio</p>
                        <p className="text-xs text-gray-500">Precisa acompanhar</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer bg-white">
                      <input
                        type="radio"
                        name="potential"
                        value="BAIXO"
                        checked={formData.potential === 'BAIXO'}
                        onChange={(e) => setFormData({ ...formData, potential: e.target.value })}
                        className="w-4 h-4"
                      />
                      <div>
                        <p className="font-medium text-sm text-red-700">Baixo</p>
                        <p className="text-xs text-gray-500"> Pouca chance de fechar</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade de Ambientes
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.environment_count}
                      onChange={(e) => setFormData({ ...formData, environment_count: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="is_multi_environment"
                      checked={formData.is_multi_environment}
                      onChange={(e) => setFormData({ ...formData, is_multi_environment: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="is_multi_environment" className="text-sm text-gray-700">
                      Múltiplos ambientes?
                    </label>
                  </div>
                </div>
              </div>

              {/* Segundo Trabalho */}
              <div className="flex items-center gap-2 p-4 bg-orange-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_second_work"
                  checked={formData.is_second_work}
                  onChange={(e) => setFormData({ ...formData, is_second_work: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_second_work" className="text-sm text-gray-700">
                  Este é um segundo trabalho para este cliente (retorno)
                </label>
              </div>

              {/* Origem */}
              <div className="mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Origem
                  </label>
                  <select
                    value={formData.origin_type}
                    onChange={(e) => setFormData({ ...formData, origin_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="NOVO">Novo Cliente</option>
                    <option value="ANTIGO">Cliente Antigo</option>
                    <option value="INDICACAO">Indicação</option>
                  </select>
                </div>
              </div>

              {/* Data Prevista para Início */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Prevista para Início
                </label>
                <input
                  type="date"
                  value={formData.desired_deadline}
                  onChange={(e) => setFormData({ ...formData, desired_deadline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Observações sobre o primeiro contato..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] disabled:opacity-50"
                >
                  {createMutation.isLoading || updateMutation.isLoading
                    ? 'Salvando...'
                    : editingItem
                    ? 'Atualizar'
                    : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Cadastro Rápido de Cliente */}
      {showClientModal && (
        <QuickClientModal
          onClose={() => setShowClientModal(false)}
          onSuccess={(newClient) => {
            // Selecionar automaticamente o cliente recém-criado
            setSelectedClient(newClient);
            setFormData({ ...formData, client_id: newClient.id });
            setShowClientModal(false);
            toast.success('Cliente cadastrado! Agora selecione-o para continuar.');
          }}
        />
      )}
    </div>
  );
};

export default ClientFirstContact;
